import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { CircularProgress, Box } from '@mui/material';
import {
  planDefinitionsQuery,
  organizationQuery,
  venueUpgradeDetailsQuery,
} from './UpgradeVenuePlanModal/graphql';

import {
  OrganizationUpgradeDetailsQuery,
  OrganizationUpgradeDetailsQueryVariables,
  VenueCustomerPlanDefinitionsQuery,
  VenueCustomerPlanDefinitionsQueryVariables,
  VenueUpgradeDetailsQuery,
  VenueUpgradeDetailsQueryVariables,
} from '@pv/common/graphql/operations';
import { UpgradeVenuePlanModalContent } from './UpgradeVenuePlanModal/UpgradeVenuePlanModalContent';
import type { UpgradeVenuePlanModalProps } from './UpgradeVenuePlanModal/types';

/**
 * Wrapper component that handles all data loading for UpgradeVenuePlanModal.
 * Once data is loaded, it renders UpgradeVenuePlanModalContent with the data.
 */
export const UpgradeVenuePlanModal = ({
  venueId,
  open,
  onClose,
  afterConfirm,
  isCreate = false,
  organizationId,
  initialBillingInterval,
  initialTier,
}: UpgradeVenuePlanModalProps) => {
  // Load organization data (includes payment methods)
  const { data: organizationData, loading: organizationLoading } = useQuery<
    OrganizationUpgradeDetailsQuery,
    OrganizationUpgradeDetailsQueryVariables
  >(organizationQuery, {
    variables: { id: organizationId || '' },
    skip: !organizationId,
  });
  const organization = organizationData?.organization;
  const paymentMethods = organization?.paymentMethods || [];

  // Load available plans
  const { data: plansData, loading: plansLoading } = useQuery<
    VenueCustomerPlanDefinitionsQuery,
    VenueCustomerPlanDefinitionsQueryVariables
  >(planDefinitionsQuery, {
    variables: { venueId: venueId || undefined },
    skip: !venueId && !isCreate,
  });
  const availablePlans = useMemo(
    () => plansData?.venueCustomerPlanDefinitions || [],
    [plansData?.venueCustomerPlanDefinitions]
  );

  // Load venue data (only if not creating)
  const { data: venueData, loading: venueLoading } = useQuery<
    VenueUpgradeDetailsQuery,
    VenueUpgradeDetailsQueryVariables
  >(venueUpgradeDetailsQuery, {
    variables: { id: venueId || '' },
    skip: !venueId || isCreate,
  });
  const venue = venueData?.venue || null;

  // Determine if we're still loading
  const loading = organizationLoading || plansLoading || venueLoading;

  // Show loading state while data is being fetched
  if (loading) {
    return (
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Once data is loaded, render the content component
  return (
    <UpgradeVenuePlanModalContent
      venue={venue}
      organization={organization || null}
      availablePlans={availablePlans}
      paymentMethods={paymentMethods}
      open={open}
      onClose={onClose}
      afterConfirm={afterConfirm}
      isCreate={isCreate}
      initialBillingInterval={initialBillingInterval}
      initialTier={initialTier}
    />
  );
};

export default UpgradeVenuePlanModal;
